/**
 * V2 Milestone awards (2026-07-18, the "more trophies" ask).
 *
 * The trophy case is competitive and losable (top-3 per category);
 * awards are the personal, permanent complement — milestone badges
 * every explorer can earn regardless of leaderboards. First batch:
 *
 *   Trailblazer   — first explorer ever to log a place
 *   Volume clubs  — total yoinks (First Yoink → Century Club → …)
 *   Completionist — collections (V1 Sets) finished end to end
 *   Checked It Off — Want-to-go places saved first, yoinked after
 *
 * Batch 2 (2026-07-18, same day) derives entirely from the journal rows
 * the profile already loads:
 *
 *   State Collector — distinct US states logged (50 = the full set)
 *   Globetrotter    — distinct countries logged
 *   Hot Streak      — most consecutive months with a yoink
 *   Four Seasons    — a yoink in spring, summer, fall, and winter
 *
 * Volume, Completionist, and batch 2 derive from data the profile
 * already loads (the journal + get_user_started_sets); Trailblazer and
 * Checked It Off come from the get_user_milestones RPC
 * (supabase/migrations/20260718b_milestone_awards.sql). Until that SQL runs, the RPC 404s
 * and those two simply stay away — the lists/trophies degradation
 * pattern.
 *
 * CELEBRATION LAYER (supabase/migrations/20260718a_user_awards.sql): earned tiers are
 * recorded to user_awards the first time they're detected — post-save
 * in the log flow (with a toast when the save itself crossed the
 * threshold) and as a quiet backfill on your own profile. Stored tiers
 * are a display FLOOR (a badge never regresses — the Checked It Off
 * cross-off case) and carry awarded_at for the "Earned March 2026"
 * line. Table not applied yet → everything degrades to computed-on-read.
 */
import * as gateway from '../../core/gateway.js';
import { cached, invalidate } from '../../core/query.js';
import { foldRegionName, isUnitedStates } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { fetchAllUserYoinks, fetchStartedSets } from './profile.js';

/** Tier names for the volume clubs — the badge renames itself as you climb. */
const VOLUME_TIERS = [
  { at: 1, name: 'First Yoink' },
  { at: 10, name: 'Ten Club' },
  { at: 50, name: 'Fifty Club' },
  { at: 100, name: 'Century Club' },
  { at: 500, name: 'Five-Hundred Club' },
];

export const AWARD_FAMILIES = [
  {
    key: 'trailblazer',
    icon: 'flag',
    color: 'red',
    tiers: [1, 10, 50, 200],
    name: () => 'Trailblazer',
    line: (n) => `First to log ${n} place${n === 1 ? '' : 's'}`,
    hint: 'Be the first explorer ever to log a place.',
  },
  {
    key: 'volume',
    icon: 'passport',
    color: 'blue',
    tiers: VOLUME_TIERS.map((t) => t.at),
    name: (tierIndex) => VOLUME_TIERS[Math.max(tierIndex, 0)].name,
    line: (n) => `${n} yoink${n === 1 ? '' : 's'} logged`,
    hint: 'Log your first yoink.',
  },
  {
    key: 'completionist',
    icon: 'rosette',
    color: 'gold',
    tiers: [1, 3, 10],
    name: () => 'Completionist',
    line: (n) => `${n} collection${n === 1 ? '' : 's'} completed`,
    hint: 'Finish every place in a collection.',
  },
  {
    key: 'checked-off',
    icon: 'bookmark-check',
    color: 'green',
    tiers: [1, 10, 25],
    name: () => 'Checked It Off',
    line: (n) => `${n} Want-to-go place${n === 1 ? '' : 's'} visited`,
    hint: 'Save a place to Want to go, then yoink it.',
  },
  {
    key: 'states',
    icon: 'map',
    color: 'green',
    tiers: [3, 10, 25, 50],
    name: () => 'State Collector',
    line: (n) => `${n} US state${n === 1 ? '' : 's'} logged`,
    hint: 'Log yoinks in 3 different US states.',
  },
  {
    key: 'countries',
    icon: 'globe',
    color: 'blue',
    tiers: [2, 5, 10],
    name: () => 'Globetrotter',
    line: (n) => `${n} countr${n === 1 ? 'y' : 'ies'} logged`,
    hint: 'Log yoinks in 2 different countries.',
  },
  {
    key: 'streak',
    icon: 'flame',
    color: 'red',
    tiers: [3, 6, 12, 24],
    name: () => 'Hot Streak',
    line: (n) => `${n} month${n === 1 ? '' : 's'} in a row`,
    hint: 'Log a yoink 3 months in a row.',
  },
  {
    key: 'seasons',
    icon: 'seasons',
    color: 'gold',
    tiers: [4],
    name: () => 'Four Seasons',
    line: (n) => (n >= 4 ? 'All four seasons logged' : `${n} of 4 seasons logged`),
    hint: 'Log a yoink in spring, summer, fall, and winter.',
  },
];

/**
 * Geography and calendar stats over the journal rows the profile
 * already loads (region fields ride FEED_SELECT since 2026-07-16;
 * created_at is the user-editable "date yoinked", so backdating
 * counts — that's how someone completes Four Seasons honestly).
 * Pure — exported for tests.
 */
export function journalStats(yoinks) {
  const states = new Set();
  const countries = new Set();
  const months = new Set();
  const seasons = new Set();
  for (const y of yoinks || []) {
    const loc = y.locations;
    // Same counting rules as the geography page (calculateLocationStats):
    // states are US-only (State Collector's top tier means all 50 actual
    // US states, not a worldwide mix of provinces), and names fold so
    // spelling variants of one region ("Bolívar"/"Bolivar",
    // "The Bahamas"/"Bahamas") stay one entry.
    if (loc?.state_province && isUnitedStates(loc)) {
      states.add(foldRegionName(loc.state_province));
    }
    if (loc?.country) countries.add(foldRegionName(loc.country));
    const d = new Date(y.created_at);
    if (!Number.isNaN(d.getTime())) {
      months.add(d.getFullYear() * 12 + d.getMonth());
      // Meteorological seasons, northern-hemisphere: Dec-Feb winter,
      // Mar-May spring, Jun-Aug summer, Sep-Nov fall
      seasons.add(Math.floor(((d.getMonth() + 1) % 12) / 3));
    }
  }
  const sorted = [...months].sort((a, b) => a - b);
  let run = sorted.length ? 1 : 0;
  let streak = run;
  for (let i = 1; i < sorted.length; i++) {
    run = sorted[i] === sorted[i - 1] + 1 ? run + 1 : 1;
    if (run > streak) streak = run;
  }
  return { states: states.size, countries: countries.size, streak, seasons: seasons.size };
}

/**
 * The two server-computed milestone counts. Shape-guarded like the
 * trophies fetch: only rows that actually look like milestones pass.
 */
export async function fetchMilestones(userId) {
  return cached(`profile-milestones:${userId}`, async () => {
    const { data, error } = await gateway.rawRpc('get_user_milestones', {
      params: { p_user_id: userId },
    });
    if (error) throw error;
    return (data || []).filter(
      (row) => typeof row?.award_key === 'string' && Number.isFinite(Number(row.value)),
    );
  });
}

/**
 * The recorded (permanent) award rows for a profile. Throws when the
 * user_awards table isn't applied yet — callers catch and treat awards
 * as computed-on-read only.
 */
export async function fetchEarnedAwardRows(userId) {
  return cached(`user-awards:${userId}`, async () => {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.USER_AWARDS, {
      select: 'award_key,tier,awarded_at',
      filters: { user_id: `eq.${userId}` },
      limit: 200,
    });
    if (error) throw error;
    return data || [];
  });
}

/**
 * Every award family with its current standing. Pure — exported for
 * tests. `earned` families sort first, in the declaration order.
 * @param {object} src
 * @param {number} src.yoinkCount - total yoinks (the journal's length)
 * @param {Array}  src.sets - get_user_started_sets rows
 * @param {Array}  src.milestones - get_user_milestones rows
 * @param {object} src.stats - journalStats() output (geography/calendar)
 * @param {Array}  src.earnedRows - user_awards rows; the highest stored
 *   tier is a display FLOOR (a recorded badge never regresses even when
 *   the live count sags — the Want-to-go cross-off case) and supplies
 *   awarded_at for the "Earned {month year}" line.
 */
export function deriveAwards({ yoinkCount = 0, sets = [], milestones = [], stats = {}, earnedRows = [] } = {}) {
  const byKey = new Map(
    (milestones || []).map((row) => [row.award_key, Number(row.value)]),
  );
  const completedSets = (sets || []).filter(
    (s) => Number(s.total_count) > 0 && Number(s.completed_count) >= Number(s.total_count),
  ).length;
  const counts = {
    trailblazer: byKey.get('trailblazer') ?? 0,
    volume: yoinkCount,
    completionist: completedSets,
    'checked-off': byKey.get('checked_off') ?? 0,
    states: stats.states || 0,
    countries: stats.countries || 0,
    streak: stats.streak || 0,
    seasons: stats.seasons || 0,
  };
  const stored = new Map(); // family key → { tier, awardedAt } (highest recorded)
  for (const row of earnedRows || []) {
    const tier = Number(row?.tier);
    if (typeof row?.award_key !== 'string' || !Number.isFinite(tier)) continue;
    const cur = stored.get(row.award_key);
    if (!cur || tier > cur.tier) stored.set(row.award_key, { tier, awardedAt: row.awarded_at || null });
  }
  const all = AWARD_FAMILIES.map((family) => {
    const floor = stored.get(family.key);
    const count = Math.max(counts[family.key] || 0, floor?.tier || 0);
    let tierIndex = -1;
    while (tierIndex + 1 < family.tiers.length && count >= family.tiers[tierIndex + 1]) tierIndex++;
    return {
      key: family.key,
      icon: family.icon,
      color: family.color,
      count,
      earned: tierIndex >= 0,
      name: family.name(tierIndex),
      line: family.line(count),
      hint: family.hint,
      nextAt: family.tiers[tierIndex + 1] ?? null,
      earnedAt: tierIndex >= 0 ? floor?.awardedAt || null : null,
    };
  });
  return [...all.filter((a) => a.earned), ...all.filter((a) => !a.earned)];
}

/** How many award families this explorer has earned (the stamp/door count). */
export function earnedCount(awards) {
  return (awards || []).filter((a) => a.earned).length;
}

/** "March 2026" for a badge card's Earned line, or '' when unparseable. */
export function earnedWhen(iso) {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

/**
 * The celebration layer: detect the signed-in explorer's current award
 * tiers, record any not yet in user_awards, and (post-save) toast the
 * ones THIS event earned. Best-effort throughout — callers .catch(()=>{}).
 *
 * Announce rule: a toast fires only when the live count sits EXACTLY on
 * the threshold — every family moves by at most 1 per save, so exact
 * match means this save crossed the line. A veteran's first sync (count
 * already past old thresholds) backfills history silently instead of
 * storming ten toasts.
 *
 * IMPORTANT ordering (log flow): call this BEFORE crossOffWantToGo —
 * the Checked It Off check must see the Want-to-go entry the visit is
 * about to remove.
 *
 * @returns {Array} the newly announced awards (empty when quiet/none)
 */
export async function syncAwards(store, userId, { announce = false, onOpen = null } = {}) {
  const me = store.get().user;
  if (!me || me.id !== userId) return []; // only ever your own
  let earnedRows;
  try {
    earnedRows = await fetchEarnedAwardRows(userId);
  } catch {
    return []; // table not applied yet — stay computed-on-read
  }
  const [yoinks, sets, milestones] = await Promise.all([
    fetchAllUserYoinks(userId),
    fetchStartedSets(userId).catch(() => []),
    fetchMilestones(userId).catch(() => []),
  ]);
  const fresh = deriveAwards({
    yoinkCount: yoinks.length, sets, milestones, stats: journalStats(yoinks),
  });
  const have = new Set(earnedRows.map((r) => `${r.award_key}:${Number(r.tier)}`));
  const inserts = [];
  const announced = [];
  for (const award of fresh) {
    const family = AWARD_FAMILIES.find((f) => f.key === award.key);
    for (const threshold of family.tiers) {
      if (award.count < threshold || have.has(`${award.key}:${threshold}`)) continue;
      inserts.push({ user_id: userId, award_key: award.key, tier: threshold });
      if (announce && award.count === threshold) announced.push(award);
    }
  }
  if (!inserts.length) return [];
  const { error } = (await gateway.rawInsert(gateway.TABLES.USER_AWARDS, { body: inserts })) || {};
  // 409 = another device recorded in between — converged, nothing to say
  if (error) return [];
  invalidate(`user-awards:${userId}`);
  for (const award of announced) {
    showToast(`Award earned — ${award.name}!`, {
      type: 'celebrate', duration: 9000, onClick: onOpen,
    });
  }
  return announced;
}
